import { autoinject } from "aurelia-dependency-injection";
import { DialogClientService } from "../../services/basic/dialog-client.service";
import type { AttachmentDialogModel, DialogAttachmentsModel } from "../../models/attachments.models";
import { AttachmentHelper } from "../../helpers/attachment.helper";
import { CompTextBox } from "../../components/comp-text-box/comp-text-box";
import { NotificationClientService, NotificationType } from "../../services/basic/notification-client.service";
import { AttachmentsClientService } from "../../services/attachments/attachments-client.service";
import { EventAggregator, type Subscription } from "aurelia-event-aggregator";

//Load styles
require("./dialog-attachments.scss");

@autoinject()
export class DialogAttachments {
    
    public dataUri: AttachmentDialogModel[] = [];
    public name: string[] = [];
    public files: FileList;
    public editing: { [index: number]: boolean } = {};
    public selectedFiles: File[] = [];
    public fileInputEl: HTMLInputElement;
    public isUploading: boolean = false;
    public entityId: number;
    public entityType: number;
    public subtaskCorrespondenceId: number;
    public reload: () => Promise<void>;
    public onSave: () => Promise<number>;
    public afterSaved: (saveResponse: any) => Promise<void>;
    private pasteSubscription: Subscription;

    constructor(private dialogClientService: DialogClientService, private notificationClientService: NotificationClientService, 
        private attachmentsClientService: AttachmentsClientService, private eventAggregator: EventAggregator) {
    }

    activate(model: DialogAttachmentsModel) {
        this.subtaskCorrespondenceId = model.subtaskCorrespondenceId;
        this.entityType = model.entityType;
        this.reload = model.reload;
        this.onSave = model.onSave;
        this.afterSaved = model.afterSaved;
        if (model.currentAttachments && model.currentAttachments.length > 0) {
            const dataTransfer = new DataTransfer();
            model.currentAttachments.forEach(attachment => { 
                this.dataUri.push({ 
                    uri: attachment.uri, 
                    name: attachment.name, 
                    path: attachment.path,
                    entityAttachmentId: attachment.entityAttachmentId, 
                    onDialogDeleted: attachment.onDialogDeleted
                });
                this.name.push(attachment.name);
                const fileToAdd = new File([attachment.uri], attachment.name);
                dataTransfer.items.add(fileToAdd);
            });

            // https://rightship.atlassian.net/browse/ET2-1372
            // Set the files for the current attachments which already been uploaded before so that we won't face with the issue of index out of range
            this.files = dataTransfer.files;
            this.setSelectedFiles(this.files);
        }
    }
    public paste: EventListenerOrEventListenerObject;

    attached() {

        this.paste = (e: any) => {
            if (!e.propagationStopped) {
                const items = ((e.clipboardData || (e.originalEvent || {}).clipboardData) || {}).items || [];
                for (const item of items) {
                    if (item.kind === 'file') {
                        const blob = item.getAsFile();
                        const reader = new FileReader();
                        reader.onload = (f) => {
                            this.eventAggregator.publish("onPaste", { source: (<any>f.target).result, fileName: blob.name });
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }
        };

        //Adds event listener for paste events
        document.addEventListener("paste", this.paste);

        this.pasteSubscription = this.eventAggregator.subscribe("onPaste", (file) => {
            const newFileUri: AttachmentDialogModel = {
                name: file.fileName,
                uri: file.source
            };

            const fileSource = file.source;
            const fileType = fileSource.substring(fileSource.indexOf(':')+1, fileSource.indexOf(';'));

            this.dataUri.push(newFileUri);

            //Get index of the file name extension's 'dot'
            const extDotIndex = file.fileName.lastIndexOf('.');
            this.name.push(file.fileName.substring(0, extDotIndex));

            const dataTransfer = new DataTransfer();

            if (this.files) {
                Array.from(this.files).forEach((file: File) => {
                    dataTransfer.items.add(file);
                });
            }

            dataTransfer.items.add([newFileUri].map(file => this.dataURLtoBlob(file.uri, fileType))[0]);
            this.files = dataTransfer.files;
            this.fileInputEl.files = dataTransfer.files;

            this.setSelectedFiles(this.files);
        });

        this.fileInputEl.addEventListener('change', (event) => {
            this.onFilesChanged(event);
        });
    }
    
    detached() {
        if (document) {
            document.removeEventListener("paste", this.paste);
        }
        if (this.pasteSubscription)
            this.pasteSubscription.dispose();
    }

    close() {
        this.dialogClientService.hide();
    }

    /**
     * Return file object's name
     * @param index Index of uploaded file
     */
    getFileName(index: number) {
        //If file name exists in list of file names, return it
        const fileName = this.name[index];
        if(fileName) return fileName;

        //Remove dot and extension from file name
        const extDotIndex = this.files[index].name.lastIndexOf('.')
        return this.files[index].name.substring(0, extDotIndex);
    }

    getFilesize(index: number, item: AttachmentDialogModel) {
        if (!item.entityAttachmentId) {
            return AttachmentHelper.formatBytes(this.files[index].size);
        }
    }


    /**
     * Edit uploaded file's name
     * @param textBox Textbox containing existing filename
     * @param index Uploaded file's index
     * @param focused If 'true', focus on the textbox, allowing file name to be changes
     */
    public editFileName(textBox: CompTextBox, index: number, focused: boolean = true) {
        this.editing[index] = !this.editing[index];
        if (!focused) {
            return;
        }
        this.getFileName(index);  
        if(textBox){
            textBox.focusTextBox();
            const input: HTMLInputElement = textBox.input;
            input.setSelectionRange(0, textBox.value.length, 'forward');
        }
    }

    /**
     * Remove file uri from uploaded files
     * @param index
     */
    async removeDataUri(index: number, item: AttachmentDialogModel) {
        if (item.entityAttachmentId) {
            await this.attachmentsClientService.deleteAttachments({ entityAttachmentIds: [item.entityAttachmentId], correspondenceGuid: null });
            item.onDialogDeleted();
        }
        this.dataUri.splice(index, 1);
        this.name.splice(index, 1);      
        this.selectedFiles.splice(index,1);  
        const dataTransfer = new DataTransfer();
        this.selectedFiles.forEach(file => {
            dataTransfer.items.add(file);
        });
        this.files = dataTransfer.files;
        if (this.fileInputEl) {
            this.fileInputEl.value = "";
        }
    }

    //When the 'Click to browse your computer' link is clicked
    public onBrowseLinkClicked() {
        this.fileInputEl.click();
    }

    public onDragOver($event: DragEvent) {
        $event.preventDefault();
    }

    public onDragDrop($event: DragEvent) {
        this.setFiles($event.dataTransfer.files);
        this.readURL(this.fileInputEl);
    }

    async save() {
        if (this.onSave && !this.subtaskCorrespondenceId) {
            this.subtaskCorrespondenceId = await this.onSave();
        }
        if (!this.dataUri) {
            this.notificationClientService.show("Error!", "File is required", NotificationType.Error);
            return;
        }

        this.isUploading = true;

        const files = this.selectedFiles.length > 0 ? this.selectedFiles : this.dataUri.map(x => {
            if (!x.entityAttachmentId) return this.dataURLtoBlob(x.uri);
        });
        const ps = [];
        let i = 0;
        // Exclude the files already uploaded before which will have the type as empty
        const newFileToSave = files.filter(x => x.type != "");
        for (const file of newFileToSave) {
            const p = this.attachmentsClientService.saveAttachments({
                entityIds: [this.subtaskCorrespondenceId],
                entityType: this.entityType,
                file: file,
                name: file.name
            });

            ps.push(p);
            i++;
        }

        try {
            const x = await Promise.all(ps)
            this.isUploading = false;
            this.notificationClientService.show("Attachment uploaded successfully", "", NotificationType.Success);
            
            if (this.afterSaved) {
                await this.afterSaved(x);
            }

            if(this.reload) {
                await this.reload();
            }
            this.close();
        }
        catch {
            this.isUploading = false;
            this.notificationClientService.show("Error!", "Attachment could not be uploaded", NotificationType.Error);
            this.close();
        }
    }

    dataURLtoBlob(dataurl, fileType: string = null) {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        let ext = mime.split("/")[1];

        if (mime == "application/zip" || mime == "application/x-zip-compressed") ext = "zip";
        if (mime == "application/vnd.openxmlformats-officedocument.wordprocessingml.document") ext = "docx";
        if (mime == "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ext = "xlsx";
        if (mime == "application/msword") ext = ".doc";
        if (mime == "application/vnd.ms-outlook") ext = ".msg";

        return new File([u8arr], `${this.name[this.dataUri.findIndex(e => e.uri == dataurl)]}.${ext}`, {
            type: fileType ?? ext
        });
    }

    /**
 * Event handler when a file is added to the files input element
 * @param event Browser event
 */
    public onFilesChanged = (event) => {
        this.setFiles(event.target.files);
        
        this.readURL(this.fileInputEl);
    };

    setFiles(fileList: FileList){
        let files = this.files==null ? [] : Array.from(this.files);
        files = files.concat(Array.from(fileList));
        const dataTransfer = new DataTransfer();
        files.forEach((file: File) => {
            dataTransfer.items.add(file); 
        });
        this.files = dataTransfer.files;
        this.fileInputEl.files = dataTransfer.files;
    }

    /**
     * Generate URIs of uploaded file objects, and update selected files
     * @param input File input element
     */
    async readURL(input: HTMLInputElement) {
        if (input.files) {    
            for(let i = 0; i < input.files.length; i++){
                const file = input.files.item(i);
                const uri = URL.createObjectURL(file);
                //If filename does not exist in the current dataUri
                if (this.dataUri.findIndex((uri: AttachmentDialogModel) => uri.name == file.name) < 0) {
                    this.dataUri.push({uri: uri, name: file.name});
                    const extDotIndex = file.name.lastIndexOf('.');

                    this.name.push(file.name.substring(0, extDotIndex));
                }
            }
      
        }
        this.setSelectedFiles(input.files);
    }

    setSelectedFiles(files: FileList) {
        this.selectedFiles = [];
        if (files && files.length > 0) {
            for(let i = 0; i < files.length; i++){
                const file = files.item(i);
                this.selectedFiles.push(file);
            }
        } else {
            this.selectedFiles = this.dataUri.map(x => this.dataURLtoBlob(x.uri));
        }
    }

    isUploadedAttachment(item: AttachmentDialogModel) {
        return item.entityAttachmentId;
    }
}